import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import {
  Moon, Sun, LogOut, Building2, Puzzle, Users, X, Plus, Layers, ScrollText, Bell, Copy,
  RefreshCw, KeyRound, Ban, Mail, MailCheck, MailX, Settings, Trash2, ArrowLeft, ArrowRight,
  Pencil, Search, FileText, BarChart3, Loader2, Link2, CheckCircle2, XCircle, Clock,
  AlertTriangle, ClipboardList, History, Hourglass, Megaphone, User, Coins, CalendarClock
} from 'lucide-react';
import DashboardSidebar from './DashboardSidebar';
import DuplicadosCandidatos from '../Shared/DuplicadosCandidatos';
import TraficoCard from './TraficoCard';
import BoletinManager from './BoletinManager';
import SolicitudesDemo from './SolicitudesDemo';
import CreditosEmpresa from './CreditosEmpresa';

// Valores del formulario de empresa. retencion_resultados_meses vacío = sin vencimiento.
// Igual que MAX_PRUEBAS_POR_STACK en backend/src/services/creditosService.js
const MAX_PRUEBAS_POR_STACK = 3;
const EMPRESA_VACIA = { nombre: '', rfc: '', limite_usuarios: '10', creditos_ilimitados: false, retencion_resultados_meses: '6' };

const Dashboard = ({ usuario, onLogout }) => {
  const [empresas, setEmpresas] = useState([]);
  const [stacks, setStacks] = useState([]);
  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showEmpresaManager, setShowEmpresaManager] = useState(false);
  const [empresaFormMode, setEmpresaFormMode] = useState('list'); // 'list' | 'create' | 'edit'
  const [filtroBusquedaEmpresa, setFiltroBusquedaEmpresa] = useState('');
  const [editingEmpresaId, setEditingEmpresaId] = useState(null);
  const [empresaCred, setEmpresaCred] = useState({ nombre: '', email: '', password: '' });
  // Edición en línea del responsable de un acceso existente
  const [editandoAcceso, setEditandoAcceso] = useState({ id: null, nombre: '' });
  const [empresaAccesos, setEmpresaAccesos] = useState([]);
  const [newCandidato, setNewCandidato] = useState({
    nombre_completo: '',
    email: '',
    curp: '',
    id_empresa: '',
    id_stack: '',
    enviar_email: true
  });
  const [newEmpresa, setNewEmpresa] = useState({ ...EMPRESA_VACIA });
  const [generatedLink, setGeneratedLink] = useState('');
  const [emailStatus, setEmailStatus] = useState({});
  const navigate = useNavigate();
  const [showStackManager, setShowStackManager] = useState(false);
  const [stackFormMode, setStackFormMode] = useState('list'); // 'list' | 'create' | 'edit'
  const [editingStackId, setEditingStackId] = useState(null);
  const [pruebasDisponibles, setPruebasDisponibles] = useState([]);
  const [newStack, setNewStack] = useState({
    nombre: '',
    descripcion: '',
    pruebas: [],
    dias_reaplicacion: ''
  });
  const [historialModal, setHistorialModal] = useState({ isOpen: false, candidatoId: null, candidato: null });
  // Edición de la retención de resultados de una asignación del historial
  const [retencionEdit, setRetencionEdit] = useState({ asignacionId: null, modo: 'empresa', fecha: '' });
  const [showLogsManager, setShowLogsManager] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsTipos, setLogsTipos] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFiltro, setLogsFiltro] = useState({ tipo: '', q: '', desde: '', hasta: '' });
  const [logsOffset, setLogsOffset] = useState(0);
  const LOGS_PAGE_SIZE = 50;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDuplicados, setShowDuplicados] = useState(false);
  const [showNotifManager, setShowNotifManager] = useState(false);
  const [notifFormMode, setNotifFormMode] = useState('list'); // 'list' | 'create'
  const [notificaciones, setNotificaciones] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [newNotif, setNewNotif] = useState({ titulo: '', mensaje: '', destinatario_tipo: 'todas', empresa_id: '', enviar_email: false });
  const [modoOscuro, setModoOscuro] = useState(() => {
    try { return localStorage.getItem('psytest_tema') === 'oscuro'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('psytest_tema', modoOscuro ? 'oscuro' : 'claro'); } catch { /* localStorage no disponible: el tema simplemente no se recuerda */ }
  }, [modoOscuro]);

  const [vista, setVista] = useState('dashboard');
  const [sidebarColapsado, setSidebarColapsado] = useState(false);

  // Navegación del sidebar: al salir de una sección cierra lo que esa
  // sección tenía abierto (mismo efecto que el botón "Cerrar" de antes) y al
  // entrar a una nueva dispara su carga de datos, igual que los botones de
  // acción que reemplaza.
  const handleNavegar = (nuevaVista) => {
    if (nuevaVista === vista) return;
    if (vista === 'empresas') cerrarGestionEmpresas();
    if (vista === 'stacks') cerrarGestionStacks();
    if (vista === 'logs') cerrarGestionLogs();
    if (vista === 'notificaciones') cerrarGestionNotificaciones();

    setVista(nuevaVista);
    if (nuevaVista === 'empresas') abrirGestionEmpresas();
    else if (nuevaVista === 'stacks') abrirGestionStacks();
    else if (nuevaVista === 'logs') abrirGestionLogs();
    else if (nuevaVista === 'notificaciones') abrirGestionNotificaciones();
  };

  // Estados para el sistema de modales
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info', // 'success', 'error', 'warning', 'info'
    title: '',
    message: '',
    onConfirm: null,
    showConfirmButton: false,
    confirmText: 'Aceptar',
    showCancelButton: false,
    cancelText: 'Cancelar',
    linkText: null // Para mostrar un enlace copiable
  });

  useEffect(() => {
    fetchData();
    cargarSolicitudesNuevas();
    // Solo se ejecuta al montar: fetchData no depende de nada que cambie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contador de solicitudes de demo nuevas para la barra lateral
  const [solicitudesNuevas, setSolicitudesNuevas] = useState(0);
  const cargarSolicitudesNuevas = async () => {
    try {
      const response = await fetch('/api/solicitudes-demo?estado=nueva', { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      setSolicitudesNuevas(data.conteos?.nueva || 0);
    } catch { /* el contador es informativo; si falla simplemente no se muestra */ }
  };

  // Se actualiza cada minuto mientras la pestaña está visible, y al volver a
  // ella, para que una solicitud nueva aparezca sin recargar el panel.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') cargarSolicitudesNuevas();
    }, 60000);
    const alVolver = () => { if (document.visibilityState === 'visible') cargarSolicitudesNuevas(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, []);

  // El letrero del enlace generado se oculta solo a los 10 segundos
  useEffect(() => {
    if (!generatedLink) return;
    const timer = setTimeout(() => setGeneratedLink(''), 10000);
    return () => clearTimeout(timer);
  }, [generatedLink]);

  const showModal = (options) => {
    setModal({
      isOpen: true,
      type: options.type || 'info',
      title: options.title || '',
      message: options.message || '',
      onConfirm: options.onConfirm || null,
      showConfirmButton: options.showConfirmButton !== false,
      confirmText: options.confirmText || 'Aceptar',
      showCancelButton: options.showCancelButton || false,
      cancelText: options.cancelText || 'Cancelar',
      linkText: options.linkText || null
    });
  };

  const closeModal = () => {
    setModal({
      isOpen: false,
      type: 'info',
      title: '',
      message: '',
      onConfirm: null,
      showConfirmButton: true,
      confirmText: 'Aceptar',
      showCancelButton: false,
      cancelText: 'Cancelar',
      linkText: null
    });
  };

  const handleModalConfirm = () => {
    if (modal.onConfirm) {
      modal.onConfirm();
    }
    closeModal();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [empresasRes, stacksRes, candidatosRes] = await Promise.all([
        fetch('/api/empresas', { credentials: 'include' }),
        fetch('/api/stacks', { credentials: 'include' }),
        fetch('/api/candidatos', { credentials: 'include' })
      ]);
      
      if (!empresasRes.ok) throw new Error('Error al cargar empresas');
      if (!stacksRes.ok) throw new Error('Error al cargar stacks');
      if (!candidatosRes.ok) throw new Error('Error al cargar candidatos');
      
      const empresasData = await empresasRes.json();
      const stacksData = await stacksRes.json();
      const candidatosData = await candidatosRes.json();
      
      setEmpresas(Array.isArray(empresasData) ? empresasData : []);
      setStacks(Array.isArray(stacksData) ? stacksData : []);
      setCandidatos(Array.isArray(candidatosData) ? candidatosData : []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
      setEmpresas([]);
      setStacks([]);
      setCandidatos([]);
      showModal({
        type: 'error',
        title: 'Error al cargar datos',
        message: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmpresaInputChange = (e) => {
    setNewEmpresa({
      ...newEmpresa,
      [e.target.name]: e.target.value
    });
  };

  const refrescarEmpresas = async () => {
    const data = await fetch('/api/empresas', { credentials: 'include' }).then(r => r.json());
    setEmpresas(Array.isArray(data) ? data : []);
  };

  const handleSubmitEmpresa = async (e) => {
    e.preventDefault();

    if (!newEmpresa.nombre.trim()) {
      showModal({ type: 'warning', title: 'Campo requerido', message: 'Debes ingresar un nombre para la empresa' });
      return;
    }
    const limiteUsuarios = parseInt(newEmpresa.limite_usuarios, 10);
    if (!Number.isInteger(limiteUsuarios) || limiteUsuarios < 1) {
      showModal({ type: 'warning', title: 'Límite de usuarios', message: 'El límite de usuarios debe ser un número mayor o igual a 1' });
      return;
    }

    try {
      let empresaId = editingEmpresaId;

      if (empresaFormMode === 'create') {
        const createResponse = await fetch('/api/empresas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ nombre: newEmpresa.nombre, rfc: newEmpresa.rfc })
        });
        const createData = await createResponse.json();
        if (!createResponse.ok) {
          showModal({ type: 'error', title: 'Error al crear empresa', message: createData.error || 'Intente nuevamente' });
          return;
        }
        empresaId = createData.id;
      }

      const putResponse = await fetch(`/api/empresas/${empresaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          nombre: newEmpresa.nombre,
          rfc: newEmpresa.rfc,
          limite_usuarios: parseInt(newEmpresa.limite_usuarios, 10),
          creditos_ilimitados: Boolean(newEmpresa.creditos_ilimitados),
          retencion_resultados_meses: newEmpresa.retencion_resultados_meses === '' ? null : parseInt(newEmpresa.retencion_resultados_meses, 10)
        })
      });
      const putData = await putResponse.json();

      if (putResponse.ok) {
        showModal({
          type: 'success',
          title: empresaFormMode === 'create' ? 'Empresa Creada' : 'Empresa Actualizada',
          message: `La empresa "${putData.nombre}" quedó configurada correctamente.`
        });
        await refrescarEmpresas();
        setEditingEmpresaId(empresaId);
        setEmpresaFormMode('edit');
      } else {
        showModal({ type: 'error', title: 'Error al guardar', message: putData.error || 'Intente nuevamente' });
      }
    } catch (error) {
      console.error('Error:', error);
      showModal({ type: 'error', title: 'Error de conexión', message: error.message });
    }
  };

  const abrirGestionEmpresas = () => {
    setShowEmpresaManager(true);
    setEmpresaFormMode('list');
    setEditingEmpresaId(null);
  };

  const cerrarGestionEmpresas = () => {
    setShowEmpresaManager(false);
    setEmpresaFormMode('list');
    setEditingEmpresaId(null);
    setNewEmpresa({ ...EMPRESA_VACIA });
    setEmpresaCred({ nombre: '', email: '', password: '' });
    setEmpresaAccesos([]);
    setFiltroBusquedaEmpresa('');
  };

  const volverAListaEmpresas = () => {
    setEmpresaFormMode('list');
    setEditingEmpresaId(null);
    setNewEmpresa({ ...EMPRESA_VACIA });
    setEmpresaCred({ nombre: '', email: '', password: '' });
    setEmpresaAccesos([]);
  };

  const cargarAccesosEmpresa = async (empresaId) => {
    try {
      const response = await fetch(`/api/empresas/${empresaId}/usuarios`, { credentials: 'include' });
      const data = await response.json();
      setEmpresaAccesos(response.ok && Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar accesos de empresa:', error);
      setEmpresaAccesos([]);
    }
  };

  const iniciarCrearEmpresa = () => {
    setNewEmpresa({ ...EMPRESA_VACIA });
    setEditingEmpresaId(null);
    setEmpresaFormMode('create');
  };

  const iniciarEditarEmpresa = async (empresa) => {
    try {
      const response = await fetch(`/api/empresas/${empresa.id}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo cargar la empresa');

      setNewEmpresa({
        nombre: data.nombre,
        rfc: data.rfc || '',
        limite_usuarios: String(data.limite_usuarios ?? 10),
        creditos_ilimitados: Boolean(data.creditos_ilimitados),
        retencion_resultados_meses: data.retencion_resultados_meses === null || data.retencion_resultados_meses === undefined ? '' : String(data.retencion_resultados_meses)
      });
      setEmpresaCred({ nombre: '', email: '', password: '' });
      setEditingEmpresaId(empresa.id);
      setEmpresaFormMode('edit');
      await cargarAccesosEmpresa(empresa.id);
    } catch (error) {
      showModal({ type: 'error', title: 'Error al cargar empresa', message: error.message });
    }
  };

  const handleGuardarAcceso = async (e) => {
    e.preventDefault();

    if (!empresaCred.nombre.trim() || !empresaCred.email.trim() || !empresaCred.password) {
      showModal({ type: 'warning', title: 'Campos requeridos', message: 'Ingresa el nombre del responsable, un email y una contraseña de al menos 8 caracteres' });
      return;
    }

    try {
      const response = await fetch(`/api/empresas/${editingEmpresaId}/usuario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ nombre: empresaCred.nombre.trim(), email: empresaCred.email, password: empresaCred.password })
      });
      const data = await response.json();

      if (response.ok) {
        showModal({
          type: 'success',
          title: 'Acceso creado',
          message: `Se creó el acceso ${data.email}. La empresa puede usarlo para iniciar sesión en su portal.`
        });
        setEmpresaCred({ nombre: '', email: '', password: '' });
        await cargarAccesosEmpresa(editingEmpresaId);
        await refrescarEmpresas();
      } else {
        showModal({ type: 'error', title: 'Error al crear acceso', message: data.error || 'Intente nuevamente' });
      }
    } catch (error) {
      showModal({ type: 'error', title: 'Error de conexión', message: error.message });
    }
  };

  const handleGuardarResponsable = async (acceso) => {
    const nombre = editandoAcceso.nombre.trim();
    if (!nombre) {
      showModal({ type: 'warning', title: 'Campo requerido', message: 'Escribe el nombre de la persona responsable.' });
      return;
    }
    try {
      const response = await fetch(`/api/empresas/${editingEmpresaId}/usuario/${acceso.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ nombre })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo guardar el responsable');
      setEditandoAcceso({ id: null, nombre: '' });
      await cargarAccesosEmpresa(editingEmpresaId);
    } catch (error) {
      showModal({ type: 'error', title: 'Error al guardar', message: error.message });
    }
  };

  const handleEliminarAcceso = (acceso) => {
    showModal({
      type: 'warning',
      title: 'Eliminar acceso',
      message: `¿Seguro que quieres eliminar el acceso "${acceso.email}"? La empresa ya no podrá usarlo para iniciar sesión.`,
      showConfirmButton: true,
      showCancelButton: true,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/empresas/${editingEmpresaId}/usuario/${acceso.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          const data = await response.json();

          if (response.ok) {
            showModal({ type: 'success', title: 'Acceso eliminado', message: `Se eliminó el acceso "${acceso.email}".` });
            await cargarAccesosEmpresa(editingEmpresaId);
            await refrescarEmpresas();
          } else {
            showModal({ type: 'error', title: 'Error al eliminar', message: data.error || 'Intente nuevamente' });
          }
        } catch (error) {
          showModal({ type: 'error', title: 'Error de conexión', message: error.message });
        }
      }
    });
  };

  const cargarPruebasDisponibles = async () => {
    try {
      console.log('Cargando pruebas disponibles...');
      const response = await fetch('/api/stacks/pruebas-catalogo', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Datos recibidos:', data);
      
      if (Array.isArray(data)) {
        setPruebasDisponibles(data);
        console.log('Pruebas cargadas:', data.length);
      } else {
        console.error('La respuesta no es un array:', data);
        setPruebasDisponibles([]);
      }
    } catch (error) {
      console.error('Error al cargar pruebas:', error);
      showModal({
        type: 'error',
        title: 'Error al cargar pruebas',
        message: error.message + '\n\nVerifica que el servidor esté corriendo y que la tabla pruebas_catalogo exista.'
      });
      setPruebasDisponibles([]);
    }
  };

  const handleStackInputChange = (e) => {
    setNewStack({
      ...newStack,
      [e.target.name]: e.target.value
    });
  };

  const handlePruebaToggle = (pruebaId) => {
    setNewStack(prev => {
      const existe = prev.pruebas.find(p => p.prueba_id === pruebaId);
      if (existe) {
        return {
          ...prev,
          pruebas: prev.pruebas.filter(p => p.prueba_id !== pruebaId)
        };
      } else if (prev.pruebas.length >= MAX_PRUEBAS_POR_STACK) {
        return prev;
      } else {
        const nuevoOrden = prev.pruebas.length;
        return {
          ...prev,
          pruebas: [...prev.pruebas, { prueba_id: pruebaId, orden: nuevoOrden }]
        };
      }
    });
  };

  const actualizarOrdenPrueba = (pruebaId, nuevoOrden) => {
    setNewStack(prev => {
      const pruebasActualizadas = prev.pruebas.map(prueba => {
        if (prueba.prueba_id === pruebaId) {
          return { ...prueba, orden: nuevoOrden };
        }
        return prueba;
      });
      
      pruebasActualizadas.sort((a, b) => a.orden - b.orden);
      
      return {
        ...prev,
        pruebas: pruebasActualizadas
      };
    });
  };

  const handleSubmitStack = async (e) => {
    e.preventDefault();

    if (!newStack.nombre.trim()) {
      showModal({
        type: 'warning',
        title: 'Campo requerido',
        message: 'Debes ingresar un nombre para el stack'
      });
      return;
    }

    if (newStack.pruebas.length === 0) {
      showModal({
        type: 'warning',
        title: 'Pruebas requeridas',
        message: 'Debes seleccionar al menos una prueba para el stack'
      });
      return;
    }

    if (newStack.pruebas.length > MAX_PRUEBAS_POR_STACK) {
      showModal({
        type: 'warning',
        title: 'Demasiadas pruebas',
        message: `Un stack puede tener máximo ${MAX_PRUEBAS_POR_STACK} pruebas. Quita ${newStack.pruebas.length - MAX_PRUEBAS_POR_STACK} para guardarlo.`
      });
      return;
    }

    const esEdicion = stackFormMode === 'edit';
    const url = esEdicion ? `/api/stacks/${editingStackId}` : '/api/stacks';
    const method = esEdicion ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          nombre: newStack.nombre,
          descripcion: newStack.descripcion,
          pruebas: newStack.pruebas,
          dias_reaplicacion: newStack.dias_reaplicacion === '' ? null : parseInt(newStack.dias_reaplicacion)
        })
      });

      const data = await response.json();

      if (response.ok) {
        showModal({
          type: 'success',
          title: esEdicion ? 'Stack Actualizado' : 'Stack Creado',
          message: `Stack "${data.nombre}" ${esEdicion ? 'actualizado' : 'creado'} exitosamente con ${data.pruebas_asignadas} pruebas.`
        });
        const stacksActualizados = await fetch('/api/stacks', { credentials: 'include' }).then(r => r.json());
        setStacks(Array.isArray(stacksActualizados) ? stacksActualizados : []);
        volverAListaStacks();
      } else {
        showModal({
          type: 'error',
          title: esEdicion ? 'Error al actualizar stack' : 'Error al crear stack',
          message: data.error || 'Intente nuevamente'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      showModal({
        type: 'error',
        title: 'Error de conexión',
        message: error.message
      });
    }
  };

  const abrirGestionStacks = () => {
    setShowStackManager(true);
    setStackFormMode('list');
    setEditingStackId(null);
    cargarPruebasDisponibles();
  };

  const cerrarGestionStacks = () => {
    setShowStackManager(false);
    setStackFormMode('list');
    setEditingStackId(null);
    setNewStack({ nombre: '', descripcion: '', pruebas: [], dias_reaplicacion: '' });
    setPruebasDisponibles([]);
  };

  const volverAListaStacks = () => {
    setStackFormMode('list');
    setEditingStackId(null);
    setNewStack({ nombre: '', descripcion: '', pruebas: [], dias_reaplicacion: '' });
  };

  const iniciarCrearStack = () => {
    setNewStack({ nombre: '', descripcion: '', pruebas: [], dias_reaplicacion: '' });
    setEditingStackId(null);
    setStackFormMode('create');
  };

  const iniciarEditarStack = async (stack) => {
    try {
      const response = await fetch(`/api/stacks/${stack.id}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo cargar el stack');

      setNewStack({
        nombre: data.nombre,
        descripcion: data.descripcion || '',
        pruebas: (data.pruebas || []).map(p => ({ prueba_id: p.id, orden: p.orden || 0 })),
        dias_reaplicacion: data.dias_reaplicacion ?? ''
      });
      setEditingStackId(stack.id);
      setStackFormMode('edit');
    } catch (error) {
      showModal({
        type: 'error',
        title: 'Error al cargar stack',
        message: error.message
      });
    }
  };

  const handleEliminarStack = (stack) => {
    showModal({
      type: 'warning',
      title: 'Eliminar Stack',
      message: `¿Seguro que quieres eliminar el stack "${stack.nombre}"? Esta acción no se puede deshacer.`,
      showConfirmButton: true,
      confirmText: 'Eliminar',
      showCancelButton: true,
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/stacks/${stack.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          const data = await response.json();

          if (response.ok) {
            setStacks(prev => prev.filter(s => s.id !== stack.id));
            showModal({
              type: 'success',
              title: 'Stack Eliminado',
              message: `El stack "${stack.nombre}" fue eliminado correctamente.`
            });
          } else {
            showModal({
              type: 'error',
              title: 'No se pudo eliminar',
              message: data.error || 'Intente nuevamente'
            });
          }
        } catch (error) {
          showModal({
            type: 'error',
            title: 'Error de conexión',
            message: error.message
          });
        }
      }
    });
  };

  const handleEliminarEmpresa = (empresa) => {
    showModal({
      type: 'warning',
      title: 'Eliminar Empresa',
      message: `¿Estás seguro de eliminar "${empresa.nombre}"? Esto borrará toda la información relacionada a la empresa, incluidos sus candidatos, pruebas y resultados asociados. Esta acción no se puede deshacer.`,
      showConfirmButton: true,
      confirmText: 'Eliminar',
      showCancelButton: true,
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/empresas/${empresa.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          const data = await response.json();

          if (response.ok) {
            setEmpresas(prev => prev.filter(e => e.id !== empresa.id));
            showModal({
              type: 'success',
              title: 'Empresa Eliminada',
              message: `La empresa "${empresa.nombre}" y toda su información relacionada fueron eliminadas correctamente.`
            });
            await fetchData();
          } else {
            showModal({
              type: 'error',
              title: 'No se pudo eliminar',
              message: data.error || 'Intente nuevamente'
            });
          }
        } catch (error) {
          showModal({
            type: 'error',
            title: 'Error de conexión',
            message: error.message
          });
        }
      }
    });
  };

  const empresasFiltradas = empresas.filter(empresa => {
    const texto = filtroBusquedaEmpresa.trim().toLowerCase();
    if (!texto) return true;
    const emails = Array.isArray(empresa.emails_acceso) ? empresa.emails_acceso : [];
    return (
      empresa.nombre?.toLowerCase().includes(texto) ||
      empresa.rfc?.toLowerCase().includes(texto) ||
      emails.some(email => email?.toLowerCase().includes(texto))
    );
  });

  const calcularTiempoRestante = (fechaLimite) => {
    if (!fechaLimite) return null;
    
    const ahora = new Date();
    const limite = new Date(fechaLimite);
    const diferencia = limite - ahora;
    
    if (diferencia <= 0) {
      return { expirado: true, texto: 'Expirado' };
    }
    
    const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24));
    const horas = Math.floor((diferencia % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (dias > 0) {
      return { expirado: false, texto: `${dias} día${dias !== 1 ? 's' : ''}`, horas };
    } else if (horas > 0) {
      return { expirado: false, texto: `${horas} hora${horas !== 1 ? 's' : ''}` };
    } else {
      return { expirado: false, texto: 'Pocas horas' };
    }
  };

  const getLinkIcon = (estado, fechaLimite) => {
    if (estado === 'completada') {
      return { Icon: CheckCircle2, color: 'var(--success-color, #48bb78)', title: 'Prueba completada' };
    }

    const tiempoRestante = calcularTiempoRestante(fechaLimite);
    if (tiempoRestante?.expirado) {
      return { Icon: XCircle, color: 'var(--danger-color, #f56565)', title: 'Enlace expirado' };
    }

    return { Icon: Link2, color: 'var(--info-color, #4299e1)', title: 'Enlace activo' };
  };

  const getEmailIcon = (emailEnviado) => {
    if (emailEnviado === true) {
      return { Icon: MailCheck, color: 'var(--success-color, #48bb78)', title: 'Correo enviado' };
    } else if (emailEnviado === false) {
      return { Icon: MailX, color: 'var(--danger-color, #f56565)', title: 'Error al enviar' };
    }
    return { Icon: Mail, color: 'var(--text-muted, #a0aec0)', title: 'No enviado' };
  };

  const getIniciales = (nombre) => {
    if (!nombre) return '?';
    const partes = nombre.trim().split(/\s+/);
    const iniciales = partes.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('');
    return iniciales || '?';
  };

  const verResultados = (candidatoId) => {
    navigate(`/resultados/${candidatoId}`);
  };

  const verDetalle = async (candidatoId) => {
    try {
      const response = await fetch(`/api/candidatos/${candidatoId}/detalle`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al obtener detalles');
      }

      setHistorialModal({ isOpen: true, candidatoId, candidato: data });
    } catch (error) {
      console.error('Error al obtener detalle:', error);
      showModal({
        type: 'error',
        title: 'Error al obtener detalles',
        message: error.message
      });
    }
  };

  const cerrarHistorial = () => {
    setHistorialModal({ isOpen: false, candidatoId: null, candidato: null });
    setRetencionEdit({ asignacionId: null, modo: 'empresa', fecha: '' });
  };

  const abrirRetencion = (asig) => {
    setRetencionEdit({
      asignacionId: asig.asignacion_id,
      modo: asig.resultados_sin_vencimiento ? 'sin_vencimiento' : asig.conservar_resultados_hasta ? 'fecha' : 'empresa',
      fecha: asig.conservar_resultados_hasta ? String(asig.conservar_resultados_hasta).slice(0, 10) : ''
    });
  };

  const guardarRetencion = async () => {
    const { asignacionId, modo, fecha } = retencionEdit;
    if (modo === 'fecha' && !fecha) {
      showModal({ type: 'warning', title: 'Fecha requerida', message: 'Indica hasta qué fecha se conservarán los resultados.' });
      return;
    }
    try {
      const response = await fetch(`/api/asignaciones/${asignacionId}/retencion`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ modo, fecha: modo === 'fecha' ? fecha : undefined })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo actualizar la retención');
      setRetencionEdit({ asignacionId: null, modo: 'empresa', fecha: '' });
      await verDetalle(historialModal.candidatoId);
    } catch (error) {
      showModal({ type: 'error', title: 'Error al guardar la retención', message: error.message });
    }
  };

  const verResultadosDeAsignacion = (candidatoId, asignacionId) => {
    cerrarHistorial();
    navigate(`/resultados/${candidatoId}?asignacion_id=${asignacionId}`);
  };

  const cargarLogs = async (offsetParam = 0, filtroParam = logsFiltro) => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: LOGS_PAGE_SIZE,
        offset: offsetParam
      });
      if (filtroParam.tipo) params.set('tipo', filtroParam.tipo);
      if (filtroParam.q) params.set('q', filtroParam.q);
      if (filtroParam.desde) params.set('desde', filtroParam.desde);
      if (filtroParam.hasta) params.set('hasta', filtroParam.hasta);

      const response = await fetch(`/api/logs?${params.toString()}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron cargar los logs');

      setLogs(data.logs || []);
      setLogsTotal(data.total || 0);
      setLogsOffset(offsetParam);
    } catch (error) {
      showModal({ type: 'error', title: 'Error al cargar logs', message: error.message });
    } finally {
      setLogsLoading(false);
    }
  };

  const abrirGestionLogs = async () => {
    setShowLogsManager(true);
    const filtroInicial = { tipo: '', q: '', desde: '', hasta: '' };
    setLogsFiltro(filtroInicial);
    cargarLogs(0, filtroInicial);
    try {
      const response = await fetch('/api/logs/tipos', { credentials: 'include' });
      const data = await response.json();
      if (response.ok) setLogsTipos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar tipos de log:', error);
    }
  };

  const cerrarGestionLogs = () => {
    setShowLogsManager(false);
    setLogs([]);
    setLogsTotal(0);
    setLogsOffset(0);
  };

  const aplicarFiltroLogs = (cambios) => {
    const nuevoFiltro = { ...logsFiltro, ...cambios };
    setLogsFiltro(nuevoFiltro);
    cargarLogs(0, nuevoFiltro);
  };

  const cargarNotificaciones = async () => {
    setNotifLoading(true);
    try {
      const response = await fetch('/api/notificaciones', { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron cargar las notificaciones');
      setNotificaciones(Array.isArray(data) ? data : []);
    } catch (error) {
      showModal({ type: 'error', title: 'Error al cargar notificaciones', message: error.message });
    } finally {
      setNotifLoading(false);
    }
  };

  const abrirGestionNotificaciones = () => {
    setShowNotifManager(true);
    setNotifFormMode('list');
    cargarNotificaciones();
  };

  const cerrarGestionNotificaciones = () => {
    setShowNotifManager(false);
    setNotifFormMode('list');
    setNewNotif({ titulo: '', mensaje: '', destinatario_tipo: 'todas', empresa_id: '', enviar_email: false });
  };

  const iniciarCrearNotificacion = () => {
    setNewNotif({ titulo: '', mensaje: '', destinatario_tipo: 'todas', empresa_id: '', enviar_email: false });
    setNotifFormMode('create');
  };

  const handleNotifInputChange = (e) => {
    const { name, value } = e.target;
    setNewNotif(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitNotif = async (e) => {
    e.preventDefault();

    if (!newNotif.titulo.trim() || !newNotif.mensaje.trim()) {
      showModal({ type: 'warning', title: 'Completa el formulario', message: 'El título y el mensaje son obligatorios.' });
      return;
    }
    if (newNotif.destinatario_tipo === 'empresa' && !newNotif.empresa_id) {
      showModal({ type: 'warning', title: 'Completa el formulario', message: 'Selecciona la empresa destinataria.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/notificaciones', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          titulo: newNotif.titulo.trim(),
          mensaje: newNotif.mensaje.trim(),
          destinatario_tipo: newNotif.destinatario_tipo,
          empresa_id: newNotif.destinatario_tipo === 'empresa' ? parseInt(newNotif.empresa_id, 10) : null,
          enviar_email: newNotif.enviar_email
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo enviar la notificación');

      setNotifFormMode('list');
      setNewNotif({ titulo: '', mensaje: '', destinatario_tipo: 'todas', empresa_id: '', enviar_email: false });
      await cargarNotificaciones();

      let mensajeExito = `Se envió "${data.titulo}" correctamente.`;
      if (data.email) {
        mensajeExito += data.email.total_destinatarios === 0
          ? ' No se envió correo: no hay usuarios registrados para ese destinatario.'
          : ` Correo enviado a ${data.email.enviados} de ${data.email.total_destinatarios} destinatario${data.email.total_destinatarios !== 1 ? 's' : ''}.`;
      }
      showModal({ type: 'success', title: 'Notificación enviada', message: mensajeExito });
    } catch (error) {
      showModal({ type: 'error', title: 'Error al enviar notificación', message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const TIPO_LOG_LABELS = {
    candidato_creado: 'Candidato creado',
    candidato_eliminado: 'Candidato eliminado',
    asignacion_creada: 'Invitación enviada',
    asignacion_renovada: 'Enlace renovado',
    prueba_iniciada: 'Prueba iniciada',
    prueba_completada: 'Prueba completada',
    empresa_creada: 'Empresa creada',
    empresa_actualizada: 'Empresa actualizada',
    acceso_creado: 'Acceso creado',
    acceso_eliminado: 'Acceso eliminado',
    stack_creado: 'Stack creado',
    stack_actualizado: 'Stack actualizado',
    stack_eliminado: 'Stack eliminado',
    notificacion_creada: 'Notificación enviada',
    candidatos_combinados: 'Candidatos combinados'
  };

  const reenviarEnlace = async (candidato) => {
    if (!candidato.token) {
      showModal({
        type: 'warning',
        title: 'Sin enlace activo',
        message: 'Este candidato no tiene un enlace activo. Debes generar una nueva asignación.'
      });
      return;
    }
    
    setEmailStatus(prev => ({ ...prev, [candidato.id]: { sending: true, sent: null } }));
    
    try {
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout de 10 segundos')), 10000)
      );
      
      const fetchPromise = fetch(`/api/asignaciones/reenviar/${candidato.token}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      const data = await response.json();
      
      if (response.ok && data.success) {
        showModal({
          type: 'success',
          title: 'Correo reenviado',
          message: `Se ha reenviado el enlace de prueba a ${candidato.email}`
        });
        setEmailStatus(prev => ({ ...prev, [candidato.id]: { sending: false, sent: true, error: false } }));
      } else {
        showModal({
          type: 'error',
          title: 'Error al reenviar',
          message: 'No se pudo enviar el correo. Intente nuevamente.'
        });
        setEmailStatus(prev => ({ ...prev, [candidato.id]: { sending: false, sent: false, error: true } }));
      }
    } catch (error) {
      console.error('Error al reenviar:', error);
      showModal({
        type: 'error',
        title: 'Error de conexión',
        message: 'Hubo un problema al reenviar el correo. Verifica tu conexión.'
      });
      setEmailStatus(prev => ({ ...prev, [candidato.id]: { sending: false, sent: false, error: true } }));
    } finally {
      setTimeout(() => {
        setEmailStatus(prev => {
          const newState = { ...prev };
          delete newState[candidato.id];
          return newState;
        });
      }, 3000);
    }
  };

  const renovarEnlace = async (candidato) => {
    if (!candidato.token) {
      showModal({
        type: 'warning',
        title: 'Sin enlace activo',
        message: 'Este candidato no tiene un enlace para renovar. Debes generar una nueva asignación.'
      });
      return;
    }

    setEmailStatus(prev => ({ ...prev, [candidato.id]: { sending: true, sent: null } }));

    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout de 10 segundos')), 10000)
      );

      const fetchPromise = fetch(`/api/asignaciones/renovar/${candidato.token}`, {
        method: 'POST',
        credentials: 'include'
      });

      const response = await Promise.race([fetchPromise, timeoutPromise]);
      const data = await response.json();

      if (response.ok && data.success) {
        showModal({
          type: 'success',
          title: 'Vigencia renovada',
          message: data.email_enviado
            ? `Se renovó el enlace por 7 días más y se reenvió el correo a ${candidato.email}. El candidato continuará exactamente donde se quedó (${candidato.pruebas_completadas || 0}/${candidato.total_pruebas_stack || 0} pruebas).`
            : `Se renovó el enlace por 7 días más, pero no se pudo reenviar el correo. Copia el enlace manualmente:`,
          linkText: data.email_enviado ? undefined : data.enlace
        });
        setEmailStatus(prev => ({ ...prev, [candidato.id]: { sending: false, sent: true, error: false } }));
        fetchData();
      } else {
        showModal({
          type: 'error',
          title: 'Error al renovar',
          message: data.error || 'No se pudo renovar la vigencia del enlace.'
        });
        setEmailStatus(prev => ({ ...prev, [candidato.id]: { sending: false, sent: false, error: true } }));
      }
    } catch (error) {
      console.error('Error al renovar:', error);
      showModal({
        type: 'error',
        title: 'Error de conexión',
        message: 'Hubo un problema al renovar el enlace. Verifica tu conexión.'
      });
      setEmailStatus(prev => ({ ...prev, [candidato.id]: { sending: false, sent: false, error: true } }));
    } finally {
      setTimeout(() => {
        setEmailStatus(prev => {
          const newState = { ...prev };
          delete newState[candidato.id];
          return newState;
        });
      }, 3000);
    }
  };

  const generarNuevoEnlace = async (candidatoId) => {
    try {
      const candidato = candidatos.find(c => c.id === candidatoId);
      
      if (!candidato || !candidato.empresa_id) {
        showModal({
          type: 'error',
          title: 'Error',
          message: 'No se pudo identificar la empresa del candidato'
        });
        return;
      }
      
      const stacksRes = await fetch('/api/stacks', {
        credentials: 'include'
      });
      const stacks = await stacksRes.json();
      
      if (stacks.length === 0) {
        showModal({
          type: 'warning',
          title: 'No hay stacks',
          message: 'No hay stacks disponibles. Crea un stack primero.'
        });
        return;
      }
      
      // Modal personalizado para seleccionar stack
      const stackOptions = stacks.map((s, i) => `${i + 1}. ${s.nombre}`).join('\n');
      const stackId = prompt(
        'Selecciona el stack para el nuevo enlace:\n' + stackOptions + '\n\nIngresa el número:'
      );
      
      if (!stackId) return;
      
      const selectedStack = stacks[parseInt(stackId) - 1];
      
      if (!selectedStack) {
        showModal({
          type: 'warning',
          title: 'Selección inválida',
          message: 'Por favor selecciona un número válido'
        });
        return;
      }
      
      const response = await fetch('/api/asignaciones', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          id_candidato: candidatoId,
          id_stack: selectedStack.id
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.email_enviado) {
          showModal({
            type: 'success',
            title: 'Nuevo enlace generado',
            message: `Se ha generado y enviado un nuevo enlace al correo del candidato.`
          });
        } else if (data.token) {
          const enlace = `${window.location.origin}/test?token=${data.token}`;
          showModal({
            type: 'info',
            title: 'Nuevo enlace generado',
            message: `Enlace generado pero no se pudo enviar por correo. Copia el enlace manualmente:`,
            linkText: enlace
          });
        }
        fetchData();
      } else {
        showModal({
          type: 'error',
          title: 'Error',
          message: data.error || 'No se pudo generar el enlace'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      showModal({
        type: 'error',
        title: 'Error de conexión',
        message: error.message
      });
    }
  };

  const copiarEnlace = (token) => {
    const enlace = `${window.location.origin}/test?token=${token}`;
    navigator.clipboard.writeText(enlace);
    showModal({
      type: 'success',
      title: 'Enlace copiado',
      message: 'El enlace ha sido copiado al portapapeles'
    });
  };

    const eliminarCandidato = async (candidatoId, nombreCandidato) => {
      showModal({
          type: 'warning',
          title: 'Confirmar eliminación',
          message: `¿Estás seguro de que deseas eliminar a "${nombreCandidato}"?\n\nEsta acción eliminará todas sus respuestas y asignaciones. No se puede deshacer.`,
          showConfirmButton: true,
          confirmText: 'Eliminar',
          showCancelButton: true,
          cancelText: 'Cancelar',
          onConfirm: async () => {
              try {
                  const response = await fetch(`/api/candidatos/${candidatoId}`, {
                      method: 'DELETE',
                      credentials: 'include'
                  });
                  
                  const data = await response.json();
                  
                  if (response.ok) {
                      showModal({
                          type: 'success',
                          title: 'Candidato eliminado',
                          message: data.message || 'Candidato eliminado correctamente'
                      });
                      
                      // 🔴 Limpiar el estado local antes de recargar
                      setCandidatos(prev => prev.filter(c => c.id !== candidatoId));
                      
                      // 🔴 Recargar datos del servidor
                      await fetchData();
                      
                  } else {
                      showModal({
                          type: 'error',
                          title: 'Error al eliminar',
                          message: data.error || 'Intente nuevamente'
                      });
                  }
              } catch (error) {
                  console.error('Error:', error);
                  showModal({
                      type: 'error',
                      title: 'Error de conexión',
                      message: error.message
                  });
              }
          }
      });
  };

  const handleInputChange = (e) => {
    setNewCandidato({
      ...newCandidato,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      showModal({
        type: 'warning',
        title: 'Procesando',
        message: 'Ya se está procesando una solicitud, por favor espera...'
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/candidatos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          nombre_completo: newCandidato.nombre_completo,
          email: newCandidato.email,
          curp: newCandidato.curp,
          id_empresa: parseInt(newCandidato.id_empresa)
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        showModal({
          type: 'error',
          title: 'Error al crear candidato',
          message: data.error || 'No se pudo crear el candidato'
        });
        setIsSubmitting(false);
        return;
      }
      
      showModal({
        type: 'success',
        title: 'Candidato Creado',
        message: `Candidato "${newCandidato.nombre_completo}" creado exitosamente.\n⏳ Generando enlace de prueba...`
      });
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('TIMEOUT')), 15000)
      );
      
      const asignacionPromise = fetch('/api/asignaciones', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          id_candidato: data.id,
          id_stack: parseInt(newCandidato.id_stack),
          enviar_email: newCandidato.enviar_email
        })
      });

      let linkData;

      try {
        const response = await Promise.race([asignacionPromise, timeoutPromise]);
        linkData = await response.json();

        if (linkData.email_enviado) {
          showModal({
            type: 'success',
            title: 'Correo Enviado',
            message: `Se ha enviado un correo con el enlace de prueba a ${newCandidato.email}`
          });
        } else if (linkData.email_omitido && linkData.token) {
          const enlace = `${window.location.origin}/test?token=${linkData.token}`;
          showModal({
            type: 'success',
            title: 'Enlace generado',
            message: `Elegiste no enviar el correo. Copia el enlace y compártelo manualmente con el candidato:`,
            linkText: enlace
          });
        } else if (linkData.token) {
          const enlace = `${window.location.origin}/test?token=${linkData.token}`;
          showModal({
            type: 'warning',
            title: 'Correo no enviado',
            message: `No se pudo enviar el correo automáticamente. Copia el enlace manualmente:`,
            linkText: enlace
          });
        }
      } catch (error) {
        console.error('Error en asignación o envío de email:', error);

        if (error.message === 'TIMEOUT') {
          showModal({
            type: 'warning',
            title: 'Tiempo de espera agotado',
            message: `La generación del enlace está tomando más tiempo de lo normal.\n\nEl candidato ha sido creado pero el enlace se generará en segundo plano. Puedes generar un enlace manualmente más tarde desde la lista de candidatos.`
          });
        } else {
          showModal({
            type: 'warning',
            title: 'Problema al generar enlace',
            message: `El candidato fue creado pero hubo un problema al generar el enlace.\n\nPuedes generar un enlace manualmente desde la lista de candidatos.`
          });
        }
      }
      
      setGeneratedLink(linkData?.token ? `${window.location.origin}/test?token=${linkData.token}` : '');
      setShowForm(false);
      setNewCandidato({
        nombre_completo: '',
        email: '',
        curp: '',
        id_empresa: '',
        id_stack: '',
        enviar_email: true
      });
      
      await fetchData();
      
    } catch (error) {
      console.error('Error en el proceso:', error);
      showModal({
        type: 'error',
        title: 'Error inesperado',
        message: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

 const copyToClipboard = (text) => {
  const stringToCopy = typeof text === 'string' ? text : String(text);
  navigator.clipboard.writeText(stringToCopy);
  showModal({
    type: 'success',
    title: 'Enlace copiado',
    message: 'El enlace ha sido copiado al portapapeles'
  });
};

  const handleLogout = async () => {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include'
    });
    onLogout();
  };

  if (loading) {
    return <div className="dashboard-loading">Cargando datos...</div>;
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h2>Error al cargar el dashboard</h2>
        <p>{error}</p>
        <button onClick={fetchData} className="btn-primary">Reintentar</button>
      </div>
    );
  }

  const candidatosFiltrados = candidatos.filter(candidato => {
    const coincideEmpresa = !filtroEmpresa || String(candidato.empresa_id) === String(filtroEmpresa);

    const texto = filtroTexto.trim().toLowerCase();
    const coincideTexto = !texto ||
      candidato.nombre_completo?.toLowerCase().includes(texto) ||
      candidato.curp?.toLowerCase().includes(texto) ||
      candidato.email?.toLowerCase().includes(texto);

    return coincideEmpresa && coincideTexto;
  });

  const hayFiltrosActivos = filtroTexto.trim() !== '' || filtroEmpresa !== '';

  const limpiarFiltros = () => {
    setFiltroTexto('');
    setFiltroEmpresa('');
  };

  return (
    <div className="dashboard-container" data-theme={modoOscuro ? 'dark' : 'light'}>
      {/* Modal Component */}
      {modal.isOpen && (
        <div className="modal-overlay modal-notification">
          <div className={`modal-content modal-notification-content modal-${modal.type}`}>
            <div className="modal-header">
              <h2>{modal.title}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="modal-message">
                {modal.message.split('\n').map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
             {modal.linkText && (
              <div className="modal-link-container">
                <input 
                  type="text" 
                  value={typeof modal.linkText === 'string' ? modal.linkText : JSON.stringify(modal.linkText)} 
                  readOnly 
                  className="modal-link-input"
                />
                <button
                  onClick={() => copyToClipboard(modal.linkText)}
                  className="btn-copy"
                >
                  <Copy size={14} /> Copiar enlace
                </button>
              </div>
            )}
            </div>
            <div className="modal-footer">
              {modal.showCancelButton && (
                <button onClick={closeModal} className="btn-secondary">
                  {modal.cancelText}
                </button>
              )}
              {modal.showConfirmButton && (
                <button onClick={handleModalConfirm} className={`btn-${modal.type}`}>
                  {modal.confirmText}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-header">
        <div className="brand">
          <img src="/isotipotransparente.png" alt="PsyTest" className="brand-logo" />
          <div className="brand-text">
            <span className="brand-title">PsyTest</span>
            <span className="brand-subtitle">Panel de administración</span>
          </div>
        </div>
        <div className="user-info">
          <button
            type="button"
            className="theme-toggle"
            onClick={() => setModoOscuro(v => !v)}
            title={modoOscuro ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {modoOscuro ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <div className="user-avatar">{getIniciales(usuario?.nombre)}</div>
          <div className="user-meta">
            <span className="user-name">{usuario?.nombre || 'Usuario'}</span>
            <span className="user-role">{usuario?.rol || 'admin'}</span>
          </div>
          <button onClick={handleLogout} className="btn-logout" title="Cerrar sesión">
            <LogOut size={14} /> <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>

      <div className="dashboard-body">
        <DashboardSidebar
          vista={vista}
          onNavegar={handleNavegar}
          colapsado={sidebarColapsado}
          onToggle={() => setSidebarColapsado(v => !v)}
          badges={{ solicitudes: solicitudesNuevas }}
        />

        <main className="dashboard-main">
          {vista === 'dashboard' && (
            <div className="dashboard-shell">
              <div className="dashboard-welcome">
                <div>
                  <span className="dash-welcome-eyebrow">Bienvenido/a</span>
                  <h1>{usuario?.nombre || 'Administrador'}</h1>
                  <p>Gestiona empresas, stacks, candidatos y la actividad de la plataforma desde un solo lugar.</p>
                </div>
                <button onClick={fetchData} className="btn-refresh">
                  <RefreshCw size={15} /> Refrescar
                </button>
              </div>

              <div className="dashboard-stats">
                <div className="stat-card">
                  <span className="stat-icon"><Building2 size={26} /></span>
                  <div className="stat-body">
                    <p className="stat-number">{empresas.length}</p>
                    <h3>Empresas</h3>
                  </div>
                </div>
                <div className="stat-card">
                  <span className="stat-icon"><Puzzle size={26} /></span>
                  <div className="stat-body">
                    <p className="stat-number">{stacks.filter(s => s.es_sugerido).length}</p>
                    <h3>Stacks sugeridos</h3>
                  </div>
                </div>
                <div className="stat-card">
                  <span className="stat-icon"><Users size={26} /></span>
                  <div className="stat-body">
                    <p className="stat-number">{candidatos.length}</p>
                    <h3>Candidatos</h3>
                  </div>
                </div>
              </div>

              <TraficoCard modoOscuro={modoOscuro} />
            </div>
          )}

      {showDuplicados && (
        <DuplicadosCandidatos
          mostrarEmpresa
          onClose={() => setShowDuplicados(false)}
          onCombinado={fetchData}
        />
      )}

      {/* Modal de gestión de empresas (stacks habilitados, créditos, acceso) */}
      {vista === 'empresas' && empresaFormMode === 'list' && (
        <div className="dashboard-shell">
          <div className="dash-page-card">
            <div className="stack-manager-header">
              <h2>Gestión de Empresas</h2>
              <button type="button" onClick={iniciarCrearEmpresa} className="btn-success">
                <Plus size={14} /> Agregar Empresa
              </button>
            </div>
            <div className="empresa-busqueda">
              <input
                type="text"
                className="filtro-busqueda"
                placeholder="Buscar por nombre, RFC o correo..."
                value={filtroBusquedaEmpresa}
                onChange={(e) => setFiltroBusquedaEmpresa(e.target.value)}
              />
            </div>
            {empresas.length === 0 ? (
              <p className="text-muted">Aún no hay empresas registradas.</p>
            ) : empresasFiltradas.length === 0 ? (
              <p className="text-muted">Ninguna empresa coincide con la búsqueda.</p>
            ) : (
              <div className="stack-list">
                {empresasFiltradas.map(empresa => (
                  <div key={empresa.id} className="stack-row">
                    <div className="stack-row-info">
                      <strong>{empresa.nombre}</strong>
                      <small>{empresa.rfc || 'Sin RFC'}</small>
                      <span className="badge-count">
                        {(empresa.stacks || []).length} stack{(empresa.stacks || []).length !== 1 ? 's' : ''}
                      </span>
                      <span
                        className={`badge-count ${!empresa.creditos_ilimitados && (empresa.creditos_disponibles ?? 0) - (empresa.creditos_reservados ?? 0) <= 0 ? 'badge-count-warn' : ''}`}
                        title="Créditos libres (reservados en invitaciones sin iniciar)"
                      >
                        <Coins size={11} />{' '}
                        {empresa.creditos_ilimitados
                          ? 'Créditos ilimitados'
                          : <>{Math.max((empresa.creditos_disponibles ?? 0) - (empresa.creditos_reservados ?? 0), 0)} créditos{empresa.creditos_reservados ? ` (${empresa.creditos_reservados} reservados)` : ''}</>}
                      </span>
                      <span className={`badge-count ${empresa.total_usuarios >= empresa.limite_usuarios ? 'badge-count-warn' : ''}`} title="Usuarios con acceso / límite de usuarios">
                        <Users size={11} /> {empresa.total_usuarios ?? 0} / {empresa.limite_usuarios ?? 10} usuarios
                      </span>
                      <span className={`badge-count ${empresa.tiene_acceso ? '' : 'badge-count-warn'}`}>
                        {empresa.tiene_acceso ? <><KeyRound size={11} /> Con acceso</> : <><Ban size={11} /> Sin acceso configurado</>}
                      </span>
                      {empresa.verificado === false && (
                        <span className="badge-count badge-count-warn" title="Se autoregistró y aún no confirma su correo">
                          <Mail size={11} /> Pendiente de verificar correo
                        </span>
                      )}
                    </div>
                    <div className="stack-row-actions">
                      <button type="button" onClick={() => iniciarEditarEmpresa(empresa)} className="btn-icon" title="Configurar">
                        <Settings size={13} /> Configurar
                      </button>
                      <button type="button" onClick={() => handleEliminarEmpresa(empresa)} className="btn-icon btn-icon-danger" title="Eliminar">
                        <Trash2 size={13} /> Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showEmpresaManager && (empresaFormMode === 'create' || empresaFormMode === 'edit') && (
        <div className="modal-overlay">
          <div className="modal-content modal-large has-sticky-header">
            <>
                <div className="modal-sticky-header">
                  <h2>{empresaFormMode === 'edit' ? 'Configurar Empresa' : 'Registrar Nueva Empresa'}</h2>
                  <div className="modal-sticky-header-actions">
                    <button type="submit" form="empresa-form" className="btn-success">
                      {empresaFormMode === 'edit' ? 'Guardar Cambios' : 'Crear Empresa'}
                    </button>
                    <button type="button" onClick={volverAListaEmpresas} className="btn-secondary">
                      <ArrowLeft size={13} /> Volver a la lista
                    </button>
                  </div>
                </div>
                <div className="modal-scroll-body">
                <form id="empresa-form" onSubmit={handleSubmitEmpresa}>
                  <div className="form-group">
                    <label>Nombre de la Empresa:</label>
                    <input
                      type="text"
                      name="nombre"
                      value={newEmpresa.nombre}
                      onChange={handleEmpresaInputChange}
                      required
                      placeholder="Ej: Empresa Ejemplo S.A. de C.V."
                    />
                  </div>
                  <div className="form-group">
                    <label>RFC:</label>
                    <input
                      type="text"
                      name="rfc"
                      value={newEmpresa.rfc}
                      onChange={handleEmpresaInputChange}
                      placeholder="Ej: ABC123456DEF"
                      maxLength="13"
                    />
                    <small className="form-hint">Opcional - 13 caracteres alfanuméricos</small>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={Boolean(newEmpresa.creditos_ilimitados)}
                        onChange={(e) => setNewEmpresa({ ...newEmpresa, creditos_ilimitados: e.target.checked })}
                      />
                      {' '}Créditos ilimitados
                    </label>
                    <small className="form-hint">
                      La empresa puede invitar sin consumir saldo. Si no, usa sus créditos asignados
                      {empresaFormMode === 'edit' ? ' (abajo puedes agregarlos)' : ' (podrás agregarlos después de crearla)'}.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Conservar resultados (meses después de finalizar):</label>
                    <input
                      type="number"
                      min="1"
                      max="120"
                      name="retencion_resultados_meses"
                      value={newEmpresa.retencion_resultados_meses}
                      onChange={handleEmpresaInputChange}
                      placeholder="Vacío = sin vencimiento"
                    />
                    <small className="form-hint">
                      Al cumplirse, se borran respuestas, puntajes y fotografía y solo queda el registro de la evaluación.
                      Por default, 6. Puedes ajustar candidatos específicos desde su historial.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Límite de usuarios (cuentas de acceso):</label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      name="limite_usuarios"
                      value={newEmpresa.limite_usuarios}
                      onChange={handleEmpresaInputChange}
                      placeholder="10"
                    />
                    <small className="form-hint">
                      Cuántas personas distintas pueden entrar al portal de esta empresa. Aplica a los accesos que crees aquí
                      y a los que la empresa cree desde su portal (Usuarios). Por default, 10.
                    </small>
                  </div>

                  <p className="form-hint">
                    ℹ️ Todas las empresas tienen acceso automático a los stacks oficiales de PsyTest,
                    además de las pruebas y stacks que ellas mismas creen desde su portal.
                  </p>
                </form>

                {empresaFormMode === 'edit' && editingEmpresaId && (
                  <CreditosEmpresa
                    empresaId={editingEmpresaId}
                    ilimitados={Boolean(newEmpresa.creditos_ilimitados)}
                    showModal={showModal}
                    onCambio={refrescarEmpresas}
                  />
                )}

                {empresaFormMode === 'edit' && (
                  <div className="empresa-acceso-box">
                    <h3>Acceso restringido de la empresa</h3>
                    <p className="form-hint">
                      Cada acceso es una cuenta independiente con la que alguien de esta empresa puede entrar a su
                      propio portal, limitado a invitar candidatos dentro de sus stacks y créditos. Puedes crear varios
                      (por ejemplo, uno por persona de RH) y eliminarlos cuando ya no se necesiten.
                    </p>

                    {empresaAccesos.length === 0 ? (
                      <p className="text-muted">Esta empresa todavía no tiene ningún acceso configurado.</p>
                    ) : (
                      <div className="stack-list">
                        {empresaAccesos.map(acceso => (
                          <div key={acceso.id} className="stack-row">
                            <div className="stack-row-info">
                              {editandoAcceso.id === acceso.id ? (
                                <div className="acceso-responsable-edicion">
                                  <input
                                    type="text"
                                    value={editandoAcceso.nombre}
                                    onChange={(e) => setEditandoAcceso({ id: acceso.id, nombre: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); handleGuardarResponsable(acceso); }
                                      if (e.key === 'Escape') setEditandoAcceso({ id: null, nombre: '' });
                                    }}
                                    placeholder="Nombre de la persona responsable"
                                    maxLength={150}
                                    autoFocus
                                  />
                                  <button type="button" className="btn-success" onClick={() => handleGuardarResponsable(acceso)}>Guardar</button>
                                  <button type="button" className="btn-secondary" onClick={() => setEditandoAcceso({ id: null, nombre: '' })}>Cancelar</button>
                                </div>
                              ) : (
                                <strong className="acceso-responsable">
                                  <User size={14} />
                                  {acceso.sin_responsable
                                    ? <span className="acceso-sin-responsable">Responsable no registrado</span>
                                    : acceso.nombre}
                                  <button
                                    type="button"
                                    className="acceso-editar"
                                    onClick={() => setEditandoAcceso({ id: acceso.id, nombre: acceso.sin_responsable ? '' : acceso.nombre })}
                                    title={acceso.sin_responsable ? 'Registrar responsable' : 'Editar responsable'}
                                  >
                                    <Pencil size={12} /> {acceso.sin_responsable ? 'Registrar' : 'Editar'}
                                  </button>
                                </strong>
                              )}
                              <span className="acceso-email"><Mail size={12} /> {acceso.email}</span>
                              <small>
                                Creado el {new Date(acceso.created_at).toLocaleDateString('es-MX')}
                                {acceso.last_login && ` · Último acceso: ${new Date(acceso.last_login).toLocaleDateString('es-MX')}`}
                                {!acceso.last_login && ' · Nunca ha iniciado sesión'}
                              </small>
                            </div>
                            <div className="stack-row-actions">
                              <button
                                type="button"
                                onClick={() => handleEliminarAcceso(acceso)}
                                className="btn-icon btn-icon-danger"
                                title="Eliminar este acceso"
                              >
                                <Trash2 size={13} /> Eliminar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <h4 className="empresa-acceso-subtitle">
                      <Plus size={14} /> Agregar nuevo acceso
                      <small className="empresa-acceso-conteo">{empresaAccesos.length} de {newEmpresa.limite_usuarios || 10} usuarios</small>
                    </h4>
                    {empresaAccesos.length >= (parseInt(newEmpresa.limite_usuarios, 10) || 10) && (
                      <p className="form-hint empresa-acceso-lleno">
                        Esta empresa ya llegó a su límite de usuarios. Para agregar otro, aumenta el "Límite de usuarios" arriba y guarda los cambios.
                      </p>
                    )}
                    <form onSubmit={handleGuardarAcceso}>
                      <div className="form-group">
                        <label>Nombre de la persona responsable:</label>
                        <input
                          type="text"
                          value={empresaCred.nombre}
                          onChange={(e) => setEmpresaCred({ ...empresaCred, nombre: e.target.value })}
                          placeholder="Ej: Ana López (Recursos Humanos)"
                          maxLength={150}
                        />
                      </div>
                      <div className="form-group">
                        <label>Email de acceso:</label>
                        <input
                          type="email"
                          value={empresaCred.email}
                          onChange={(e) => setEmpresaCred({ ...empresaCred, email: e.target.value })}
                          placeholder="contacto@empresa.com"
                        />
                      </div>
                      <div className="form-group">
                        <label>Contraseña:</label>
                        <input
                          type="password"
                          value={empresaCred.password}
                          onChange={(e) => setEmpresaCred({ ...empresaCred, password: e.target.value })}
                          placeholder="Mínimo 8 caracteres"
                        />
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="btn-success"><Plus size={14} /> Crear Acceso</button>
                      </div>
                    </form>
                  </div>
                )}
                </div>
              </>
          </div>
        </div>
      )}

      {vista === 'stacks' && stackFormMode === 'list' && (
        <div className="dashboard-shell">
          <div className="dash-page-card">
            <div className="stack-manager-header">
              <h2>Stacks sugeridos</h2>
              <button type="button" onClick={iniciarCrearStack} className="btn-success">
                <Plus size={14} /> Agregar stack sugerido
              </button>
            </div>
            <p className="stacks-sugeridos-nota">
              Están disponibles para todas las empresas. La reaplicación que definas aquí es solo una sugerencia:
              cada empresa puede ajustarla para sus propios candidatos desde su portal.
            </p>

            {stacks.filter(s => s.es_sugerido).length === 0 ? (
              <p className="text-muted">Aún no hay stacks sugeridos.</p>
            ) : (
              <div className="stack-list">
                {stacks.filter(s => s.es_sugerido).map(stack => (
                  <div key={stack.id} className="stack-row">
                    <div className="stack-row-info">
                      <strong>{stack.nombre}</strong>
                      <small>{stack.descripcion || 'Sin descripción'}</small>
                      <span className="badge-count">{stack.total_pruebas ?? 0} pruebas</span>
                      <span className="badge-count badge-reaplicacion">
                        <RefreshCw size={11} />
                        {stack.dias_reaplicacion
                          ? ` Reaplicación sugerida: cada ${stack.dias_reaplicacion} día${stack.dias_reaplicacion !== 1 ? 's' : ''}`
                          : ' Reaplicación sugerida: sin límite'}
                      </span>
                      {stack.empresas_con_ajuste > 0 && (
                        <span className="badge-count" title="Empresas que fijaron su propia regla de reaplicación para este stack">
                          <Building2 size={11} /> {stack.empresas_con_ajuste} empresa{stack.empresas_con_ajuste !== 1 ? 's' : ''} con ajuste propio
                        </span>
                      )}
                    </div>
                    <div className="stack-row-actions">
                      <button type="button" onClick={() => iniciarEditarStack(stack)} className="btn-icon" title="Editar">
                        <Pencil size={13} /> Editar
                      </button>
                      <button type="button" onClick={() => handleEliminarStack(stack)} className="btn-icon btn-icon-danger" title="Eliminar">
                        <Trash2 size={13} /> Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de gestión de stacks (CRUD) */}
      {showStackManager && (stackFormMode === 'create' || stackFormMode === 'edit') && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <>
                <h2>{stackFormMode === 'edit' ? 'Editar stack sugerido' : 'Nuevo stack sugerido'}</h2>
                <form onSubmit={handleSubmitStack}>
                  <div className="form-group">
                    <label>Nombre del Stack:</label>
                    <input
                      type="text"
                      name="nombre"
                      value={newStack.nombre}
                      onChange={handleStackInputChange}
                      required
                      placeholder="Ej: Desarrollo Frontend"
                    />
                  </div>

                  <div className="form-group">
                    <label>Descripción:</label>
                    <textarea
                      name="descripcion"
                      value={newStack.descripcion}
                      onChange={handleStackInputChange}
                      placeholder="Ej: Batería de pruebas para desarrolladores frontend"
                      rows="2"
                    />
                  </div>

                  <div className="form-group">
                    <label>Reaplicación sugerida (días de espera):</label>
                    <input
                      type="number"
                      name="dias_reaplicacion"
                      min="1"
                      value={newStack.dias_reaplicacion}
                      onChange={handleStackInputChange}
                      placeholder="Vacío = sin límite (se pueden generar tantas ligas como se requiera)"
                    />
                    <small className="form-hint">
                      Vacío = se sugiere reaplicar sin límite (cada intento guarda sus propios resultados).
                      Con un número, se sugiere esperar esos días desde el último intento del candidato.
                      Es solo la regla por defecto: cada empresa puede cambiarla para sus candidatos.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Seleccionar Pruebas ({newStack.pruebas.length}/{MAX_PRUEBAS_POR_STACK}):</label>
                    <small className="form-hint">
                      Máximo {MAX_PRUEBAS_POR_STACK} pruebas por stack; invitar a un stack cuesta 2 créditos.
                      {newStack.pruebas.length > MAX_PRUEBAS_POR_STACK && <strong> Este stack excede el máximo: quita pruebas para guardarlo.</strong>}
                    </small>
                    <div className="pruebas-grid">
                      {pruebasDisponibles.length === 0 ? (
                        <p className="text-muted">No hay pruebas disponibles en el catálogo</p>
                      ) : (
                        pruebasDisponibles.map(prueba => {
                          const estaSeleccionada = newStack.pruebas.some(p => p.prueba_id === prueba.id);
                          const ordenActual = newStack.pruebas.find(p => p.prueba_id === prueba.id)?.orden;

                          return (
                            <div key={prueba.id} className="prueba-item">
                              <label className="prueba-checkbox">
                                <input
                                  type="checkbox"
                                  checked={estaSeleccionada}
                                  disabled={!estaSeleccionada && newStack.pruebas.length >= MAX_PRUEBAS_POR_STACK}
                                  onChange={() => handlePruebaToggle(prueba.id)}
                                />
                                <div className="prueba-info">
                                  <strong>{prueba.nombre}</strong>
                                  <small>{prueba.descripcion || 'Sin descripción'}</small>
                                </div>
                              </label>
                              {estaSeleccionada && (
                                <div className="prueba-orden">
                                  <label>Orden:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={ordenActual}
                                    onChange={(e) => actualizarOrdenPrueba(prueba.id, parseInt(e.target.value))}
                                    className="orden-input"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-success">
                      {stackFormMode === 'edit' ? 'Guardar Cambios' : `Crear Stack (${newStack.pruebas.length} pruebas)`}
                    </button>
                    <button type="button" onClick={volverAListaStacks} className="btn-secondary">
                      <ArrowLeft size={13} /> Volver a la lista
                    </button>
                  </div>
                </form>
              </>
          </div>
        </div>
      )}

      {/* Modal para crear nuevo candidato */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Registrar Nuevo Candidato</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre Completo:</label>
                <input
                  type="text"
                  name="nombre_completo"
                  value={newCandidato.nombre_completo}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="email"
                  value={newCandidato.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label>CURP:</label>
                <input
                  type="text"
                  name="curp"
                  value={newCandidato.curp}
                  onChange={handleInputChange}
                  required
                  maxLength="18"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label>Empresa:</label>
                <select
                  name="id_empresa"
                  value={newCandidato.id_empresa}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Seleccionar empresa</option>
                  {empresas.map(empresa => (
                    <option key={empresa.id} value={empresa.id}>
                      {empresa.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Stack de Pruebas:</label>
                <select
                  name="id_stack"
                  value={newCandidato.id_stack}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Seleccionar stack</option>
                  {stacks.map(stack => (
                    <option key={stack.id} value={stack.id}>
                      {stack.es_sugerido ? stack.nombre : `${stack.nombre} (propio de ${stack.empresa_nombre || 'una empresa'})`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group form-group-checkbox">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="enviar_email"
                    checked={newCandidato.enviar_email}
                    onChange={(e) => setNewCandidato({ ...newCandidato, enviar_email: e.target.checked })}
                    disabled={isSubmitting}
                  />
                  Enviar correo de invitación al candidato
                </label>
                {!newCandidato.enviar_email && (
                  <small className="form-hint">
                    No se enviará correo. Podrás copiar el enlace de la prueba manualmente al terminar.
                  </small>
                )}
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-success" disabled={isSubmitting}>
                  {isSubmitting ? (<><Loader2 size={14} className="spin-icon" /> Procesando...</>) : 'Guardar'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary" disabled={isSubmitting}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {vista === 'candidatos' && (
      <div className="dashboard-shell">
      {generatedLink && (
        <div className="link-generated">
          <h3>¡Enlace de prueba generado!</h3>
          <div className="link-box">
            <input type="text" value={generatedLink} readOnly />
            <button onClick={() => copyToClipboard(generatedLink)} className="btn-copy"><Copy size={14} /> Copiar</button>
          </div>
        </div>
      )}

      <div className="candidatos-list">
        <div className="candidatos-list-header">
          <h2>Candidatos Registrados</h2>
          <span className="candidatos-count">
            {hayFiltrosActivos ? `${candidatosFiltrados.length} de ${candidatos.length}` : candidatos.length}
          </span>
          <div className="dash-page-card-actions">
            <button onClick={() => setShowForm(!showForm)} className="btn-primary" disabled={isSubmitting}>
              {showForm ? <><X size={15} /> Cancelar</> : <><Plus size={15} /> Nuevo Candidato</>}
            </button>
            <button onClick={() => setShowDuplicados(true)} className="btn-logs">
              <Copy size={15} /> Duplicados
            </button>
          </div>
        </div>

        <div className="candidatos-filtros">
          <input
            type="text"
            className="filtro-busqueda"
            placeholder="Buscar por nombre, CURP o email..."
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
          />
          <select
            className="filtro-empresa"
            value={filtroEmpresa}
            onChange={(e) => setFiltroEmpresa(e.target.value)}
          >
            <option value="">Todas las empresas</option>
            {empresas.map(empresa => (
              <option key={empresa.id} value={empresa.id}>{empresa.nombre}</option>
            ))}
          </select>
          {hayFiltrosActivos && (
            <button type="button" onClick={limpiarFiltros} className="btn-limpiar-filtros">
              <X size={13} /> Limpiar filtros
            </button>
          )}
        </div>

        {candidatos.length === 0 ? (
          <p className="empty-state">No hay candidatos registrados.</p>
        ) : candidatosFiltrados.length === 0 ? (
          <p className="empty-state">Ningún candidato coincide con los filtros aplicados.</p>
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr> 
                  <th>Nombre</th>
                  <th>CURP</th>
                  <th>Empresa</th>
                  <th>Email</th>
                  <th>Enlace de Prueba</th>
                  <th>Tiempo Restante</th>
                  <th>Email</th>
                  <th>Estado</th>
                  <th>Pruebas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {candidatosFiltrados.map(candidato => {
                  const tiempoRestante = calcularTiempoRestante(candidato.fecha_limite);
                  const linkIcon = getLinkIcon(candidato.estado, candidato.fecha_limite);
                  const emailIcon = getEmailIcon(candidato.email_enviado);
                  const emailStatusInfo = emailStatus[candidato.id];
                  
                  return (
                    <tr key={candidato.id} className={candidato.estado === 'completada' ? 'row-completada' : ''}>
                      
                      <td>
                        <div className="candidato-cell">
                          <span className="candidato-avatar">
                            {candidato.foto_url ? (
                              <img src={candidato.foto_url} alt="" className="candidato-avatar-foto" />
                            ) : (
                              getIniciales(candidato.nombre_completo)
                            )}
                          </span>
                          <span>{candidato.nombre_completo}</span>
                        </div>
                      </td>
                      <td><code className="curp-code">{candidato.curp}</code></td>
                      <td>{candidato.empresa_nombre || 'Sin empresa'}</td>
                      <td>{candidato.email}</td>
                      <td>
                        {candidato.token && candidato.estado !== 'completada' && !tiempoRestante?.expirado ? (
                          <div className="link-cell">
                            <code className="link-code">{candidato.token.substring(0, 8)}...</code>
                            <button 
                              onClick={() => copiarEnlace(candidato.token)}
                              className="btn-copy-link"
                              title="Copiar enlace"
                            >
                              <Copy size={13} />
                            </button>
                          </div>
                        ) : (
                          <span className="no-link">Sin enlace activo</span>
                        )}
                      </td>
                      <td>
                        {candidato.fecha_limite && candidato.estado !== 'completada' ? (
                          <div className="time-cell">
                            <span 
                              className={`time-badge ${tiempoRestante?.expirado ? 'expirado' : 'activo'}`}
                              title={tiempoRestante?.expirado ? 'Enlace expirado' : `Expira en ${tiempoRestante?.texto}`}
                            >
                              {tiempoRestante?.expirado ? (<><XCircle size={11} /> Expirado</>) : (<><Clock size={11} /> {tiempoRestante?.texto}</>)}
                            </span>
                          </div>
                        ) : (
                          <span className="completed-badge"><CheckCircle2 size={11} /> Completada</span>
                        )}
                      </td>
                      <td>
                        <div className="email-cell">
                          <span 
                            className={`email-status ${emailStatusInfo?.sent === true ? 'sent' : emailStatusInfo?.sent === false ? 'error' : ''}`}
                            title={emailIcon.title}
                          >
                            {emailStatusInfo?.sending ? (
                              <span className="sending"><Loader2 size={13} className="spin-icon" /> Enviando...</span>
                            ) : (
                              <span style={{ color: emailIcon.color }}><emailIcon.Icon size={16} /></span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge status-${candidato.estado || 'pendiente'}`}>
                          <span className="status-icon" style={{ color: linkIcon.color }}><linkIcon.Icon size={13} /></span>
                          {candidato.estado === 'completada' ? 'Completada' : 'Pendiente'}
                        </span>
                        {candidato.riesgo_fraude && (
                          <span
                            className={`badge-riesgo badge-riesgo-${candidato.riesgo_fraude}`}
                            title={`${candidato.riesgo_fraude === 'rojo' ? 'Muy probable' : 'Probable'} indicio de fraude en sus respuestas: ${candidato.cambios_pestana} cambio(s) de pestaña y ${candidato.respuestas_rapidas} respuesta(s) sospechosamente rápida(s) durante pruebas de habilidad.`}
                          >
                            <AlertTriangle size={11} /> Riesgo
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="respuestas-badge">
                          <ClipboardList size={11} /> {candidato.pruebas_completadas || 0}/{candidato.total_pruebas_stack || 0}
                        </span>
                        {candidato.total_asignaciones > 1 && (
                          <button
                            type="button"
                            className="badge-intentos"
                            onClick={() => verDetalle(candidato.id)}
                            title="Ver historial de todos los stacks aplicados a este candidato"
                          >
                            <History size={11} /> {candidato.total_asignaciones} intentos
                          </button>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            onClick={() => verDetalle(candidato.id)}
                            className="btn-detail"
                            title="Ver detalle del candidato"
                          >
                            <FileText size={14} />
                          </button>
                          {candidato.estado === 'completada' && (
                            <button
                              onClick={() => verResultados(candidato.id)}
                              className="btn-results"
                              title="Ver resultados"
                            >
                              <BarChart3 size={14} />
                            </button>
                          )}
                          {candidato.token && candidato.estado !== 'completada' && !tiempoRestante?.expirado && (
                            <button
                              onClick={() => reenviarEnlace(candidato)}
                              className="btn-resend"
                              title="Reenviar enlace por correo"
                              disabled={emailStatusInfo?.sending}
                            >
                              {emailStatusInfo?.sending ? <Loader2 size={14} className="spin-icon" /> : <Mail size={14} />}
                            </button>
                          )}
                          {candidato.token && candidato.estado !== 'completada' && tiempoRestante?.expirado && (
                            <button
                              onClick={() => renovarEnlace(candidato)}
                              className="btn-renew"
                              title="Renovar vigencia (el candidato continúa donde se quedó)"
                              disabled={emailStatusInfo?.sending}
                            >
                              {emailStatusInfo?.sending ? <Loader2 size={14} className="spin-icon" /> : <RefreshCw size={14} />}
                            </button>
                          )}
                          <button
                            onClick={() => generarNuevoEnlace(candidato.id)}
                            className="btn-new-link"
                            title="Generar nuevo enlace (reinicia el progreso con un stack nuevo)"
                          >
                            <Link2 size={14} />
                          </button>
                          <button
                            onClick={() => eliminarCandidato(candidato.id, candidato.nombre_completo)}
                            className="btn-delete"
                            title="Eliminar candidato"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
      )}

      {historialModal.isOpen && historialModal.candidato && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="stack-manager-header">
              <h2>Historial de {historialModal.candidato.nombre_completo}</h2>
              <button type="button" onClick={cerrarHistorial} className="btn-icon" title="Cerrar"><X size={14} /></button>
            </div>
            <p className="text-muted">
              {historialModal.candidato.email} · {historialModal.candidato.empresa_nombre}
            </p>

            {(!historialModal.candidato.asignaciones || historialModal.candidato.asignaciones.length === 0) ? (
              <p className="text-muted">Aún no tiene asignaciones.</p>
            ) : (
              <div className="stack-list">
                {historialModal.candidato.asignaciones.map(asig => (
                  <div key={asig.asignacion_id} className="stack-row">
                    <div className="stack-row-info">
                      <strong>{asig.stack_nombre || 'Stack eliminado'}</strong>
                      <small>
                        Asignado el {new Date(asig.fecha_asignacion).toLocaleDateString('es-MX')}
                        {asig.fecha_completada && ` · Completado el ${new Date(asig.fecha_completada).toLocaleDateString('es-MX')}`}
                      </small>
                      <span className={`status-badge status-${asig.estado || 'pendiente'}`}>
                        {asig.estado === 'completada' ? (<><CheckCircle2 size={12} /> Completada</>) : (<><Hourglass size={12} /> Pendiente</>)}
                      </span>
                      {asig.resultados_eliminados_at ? (
                        <small className="retencion-linea retencion-eliminada">
                          Resultados eliminados el {new Date(asig.resultados_eliminados_at).toLocaleDateString('es-MX')} (fin del periodo de conservación)
                        </small>
                      ) : asig.estado === 'completada' && (
                        <small className="retencion-linea">
                          <CalendarClock size={12} />{' '}
                          {asig.fecha_eliminacion_resultados
                            ? `Resultados se conservan hasta el ${new Date(asig.fecha_eliminacion_resultados).toLocaleDateString('es-MX')}`
                            : 'Resultados sin vencimiento'}
                          {asig.resultados_sin_vencimiento || asig.conservar_resultados_hasta ? ' (ajuste manual)' : ''}
                        </small>
                      )}
                      {retencionEdit.asignacionId === asig.asignacion_id && (
                        <div className="retencion-editor">
                          <select value={retencionEdit.modo} onChange={(e) => setRetencionEdit({ ...retencionEdit, modo: e.target.value })}>
                            <option value="empresa">Según la empresa</option>
                            <option value="fecha">Hasta una fecha</option>
                            <option value="sin_vencimiento">Sin vencimiento</option>
                          </select>
                          {retencionEdit.modo === 'fecha' && (
                            <input type="date" value={retencionEdit.fecha} onChange={(e) => setRetencionEdit({ ...retencionEdit, fecha: e.target.value })} />
                          )}
                          <button type="button" className="btn-success" onClick={guardarRetencion}>Guardar</button>
                          <button type="button" className="btn-secondary" onClick={() => setRetencionEdit({ asignacionId: null, modo: 'empresa', fecha: '' })}>Cancelar</button>
                        </div>
                      )}
                    </div>
                    <div className="stack-row-actions">
                      {asig.estado === 'completada' && !asig.resultados_eliminados_at && retencionEdit.asignacionId !== asig.asignacion_id && (
                        <button type="button" onClick={() => abrirRetencion(asig)} className="btn-icon" title="Cambiar cuánto tiempo se conservan los resultados">
                          <CalendarClock size={13} /> Retención
                        </button>
                      )}
                      {asig.estado === 'completada' && !asig.resultados_eliminados_at && (
                        <button
                          type="button"
                          onClick={() => verResultadosDeAsignacion(historialModal.candidatoId, asig.asignacion_id)}
                          className="btn-icon"
                          title="Ver resultados de este intento"
                        >
                          <BarChart3 size={13} /> Ver resultados
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="form-actions">
              <button type="button" onClick={cerrarHistorial} className="btn-secondary">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Página de bitácora de actividad (solo lectura, sin opción de borrar) */}
      {vista === 'logs' && (
        <div className="dashboard-shell">
          <div className="dash-page-card">
            <div className="stack-manager-header">
              <h2><ScrollText size={18} /> Bitácora de actividad</h2>
              <button type="button" onClick={() => cargarLogs(logsOffset, logsFiltro)} className="btn-secondary" disabled={logsLoading}>
                <RefreshCw size={14} /> Refrescar
              </button>
            </div>
            <div>
              <div className="logs-filtros">
                <input
                  type="text"
                  placeholder="Buscar por nombre, descripción..."
                  value={logsFiltro.q}
                  onChange={(e) => setLogsFiltro({ ...logsFiltro, q: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && aplicarFiltroLogs({ q: logsFiltro.q })}
                />
                <select
                  value={logsFiltro.tipo}
                  onChange={(e) => aplicarFiltroLogs({ tipo: e.target.value })}
                >
                  <option value="">Todos los tipos de evento</option>
                  {logsTipos.map(tipo => (
                    <option key={tipo} value={tipo}>{TIPO_LOG_LABELS[tipo] || tipo}</option>
                  ))}
                </select>
                <button type="button" onClick={() => aplicarFiltroLogs({ q: logsFiltro.q })} className="btn-secondary">
                  <Search size={14} /> Buscar
                </button>
                <div className="logs-filtro-fechas">
                  <label>
                    Desde
                    <input
                      type="date"
                      value={logsFiltro.desde}
                      max={logsFiltro.hasta || undefined}
                      onChange={(e) => aplicarFiltroLogs({ desde: e.target.value })}
                    />
                  </label>
                  <label>
                    Hasta
                    <input
                      type="date"
                      value={logsFiltro.hasta}
                      min={logsFiltro.desde || undefined}
                      onChange={(e) => aplicarFiltroLogs({ hasta: e.target.value })}
                    />
                  </label>
                  {(logsFiltro.desde || logsFiltro.hasta) && (
                    <button type="button" onClick={() => aplicarFiltroLogs({ desde: '', hasta: '' })} className="btn-icon" title="Quitar filtro de fechas">
                      <X size={13} /> Fechas
                    </button>
                  )}
                </div>
              </div>

              <p className="text-muted">
                {logsTotal} registro{logsTotal !== 1 ? 's' : ''} en total. Esta bitácora es solo de lectura: no hay forma de borrar entradas desde el panel.
              </p>

              {logsLoading ? (
                <p className="text-muted">Cargando...</p>
              ) : logs.length === 0 ? (
                <p className="text-muted">No hay registros que coincidan con el filtro.</p>
              ) : (
                <div className="logs-list">
                  {logs.map(log => (
                    <div key={log.id} className="log-row">
                      <div className="log-row-tipo">{TIPO_LOG_LABELS[log.tipo] || log.tipo}</div>
                      <div className="log-row-desc">{log.descripcion}</div>
                      <div className="log-row-fecha">
                        {new Date(log.created_at).toLocaleString('es-MX')}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="logs-paginacion">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={logsOffset === 0 || logsLoading}
                  onClick={() => cargarLogs(Math.max(0, logsOffset - LOGS_PAGE_SIZE), logsFiltro)}
                >
                  <ArrowLeft size={14} /> Más recientes
                </button>
                <span className="text-muted">
                  {logsTotal === 0 ? '0' : `${logsOffset + 1}–${Math.min(logsOffset + LOGS_PAGE_SIZE, logsTotal)}`} de {logsTotal}
                </span>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={logsOffset + LOGS_PAGE_SIZE >= logsTotal || logsLoading}
                  onClick={() => cargarLogs(logsOffset + LOGS_PAGE_SIZE, logsFiltro)}
                >
                  Más antiguos <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {vista === 'notificaciones' && notifFormMode === 'list' && (
        <div className="dashboard-shell">
          <div className="dash-page-card">
            <div className="stack-manager-header">
              <h2><Bell size={18} /> Notificaciones</h2>
              <button type="button" onClick={iniciarCrearNotificacion} className="btn-success">
                <Plus size={14} /> Nueva notificación
              </button>
            </div>

            {notifLoading ? (
              <p className="text-muted">Cargando...</p>
            ) : notificaciones.length === 0 ? (
              <p className="text-muted">Aún no se ha enviado ninguna notificación.</p>
            ) : (
              <div className="stack-list">
                {notificaciones.map(notif => (
                  <div key={notif.id} className="stack-row">
                    <div className="stack-row-info">
                      <strong>{notif.titulo}</strong>
                      <small>{notif.mensaje}</small>
                      <span className="badge-count">
                        {notif.empresa_id ? (<><Building2 size={11} /> {notif.empresa_nombre || 'Empresa'}</>) : (<><Megaphone size={11} /> Todas las empresas</>)}
                      </span>
                      <span className="badge-count">
                        {new Date(notif.created_at).toLocaleString('es-MX')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <BoletinManager showModal={showModal} modoOscuro={modoOscuro} />
        </div>
      )}

      {vista === 'solicitudes' && (
        <SolicitudesDemo showModal={showModal} onCambio={cargarSolicitudesNuevas} />
      )}

      {showNotifManager && notifFormMode === 'create' && (
        <div className="modal-overlay">
          <div className="modal-content modal-large has-sticky-header">
            <>
                <div className="modal-sticky-header">
                  <h2>Nueva notificación</h2>
                  <div className="modal-sticky-header-actions">
                    <button type="submit" form="notif-form" className="btn-success" disabled={isSubmitting}>
                      {isSubmitting ? 'Enviando...' : 'Enviar notificación'}
                    </button>
                    <button type="button" onClick={() => setNotifFormMode('list')} className="btn-secondary">
                      <ArrowLeft size={13} /> Volver a la lista
                    </button>
                  </div>
                </div>
                <div className="modal-scroll-body">
                  <form id="notif-form" onSubmit={handleSubmitNotif}>
                    <div className="form-group">
                      <label>Título:</label>
                      <input
                        type="text"
                        name="titulo"
                        value={newNotif.titulo}
                        onChange={handleNotifInputChange}
                        required
                        disabled={isSubmitting}
                        placeholder="Ej: Mantenimiento programado"
                      />
                    </div>
                    <div className="form-group">
                      <label>Mensaje:</label>
                      <textarea
                        name="mensaje"
                        value={newNotif.mensaje}
                        onChange={handleNotifInputChange}
                        required
                        disabled={isSubmitting}
                        rows={4}
                        placeholder="Contenido de la notificación..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Destinatario:</label>
                      <select
                        name="destinatario_tipo"
                        value={newNotif.destinatario_tipo}
                        onChange={handleNotifInputChange}
                        disabled={isSubmitting}
                      >
                        <option value="todas">Todas las empresas</option>
                        <option value="empresa">Empresa específica</option>
                      </select>
                    </div>
                    {newNotif.destinatario_tipo === 'empresa' && (
                      <div className="form-group">
                        <label>Empresa:</label>
                        <select
                          name="empresa_id"
                          value={newNotif.empresa_id}
                          onChange={handleNotifInputChange}
                          required
                          disabled={isSubmitting}
                        >
                          <option value="">Seleccionar empresa</option>
                          {empresas.map(empresa => (
                            <option key={empresa.id} value={empresa.id}>
                              {empresa.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="form-group form-group-checkbox">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="enviar_email"
                          checked={newNotif.enviar_email}
                          onChange={(e) => setNewNotif(prev => ({ ...prev, enviar_email: e.target.checked }))}
                          disabled={isSubmitting}
                        />
                        Enviar también por correo electrónico
                      </label>
                      {newNotif.enviar_email && (
                        <small className="form-hint">
                          Se enviará a {newNotif.destinatario_tipo === 'empresa' ? 'los usuarios con acceso a la empresa seleccionada' : 'los usuarios con acceso de todas las empresas'}.
                        </small>
                      )}
                    </div>
                  </form>
                </div>
              </>
          </div>
        </div>
      )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;